import parse from 'html-react-parser'
import Head from 'next/head'

import { FRONT_BASE_URL } from '@/constants'

import { WPPageData } from '@/types'

export type MetaProps = {
  pageProps: WPPageData
}

const BASE_URL = FRONT_BASE_URL?.replace(/\/$/, '')

const ensureAbsoluteCanonical = (url?: string) => {
  if (!url || /^https?:\/\//i.test(url) || !BASE_URL) return url

  const normalizedPath = url.startsWith('/') ? url : `/${url}`
  return `${BASE_URL}${normalizedPath}`
}

const getCanonicalFromHead = (head?: string) => {
  return head?.match(/<link\s+rel="canonical"\s+href="([^"]+)"\s*\/?/i)?.[1]
}

const getCanonicalUrl = (pageProps: WPPageData) => {
  const canonicalFromJson = pageProps?.yoast_head_json?.canonical
  const canonicalFromHead = getCanonicalFromHead(pageProps?.yoast_head)

  return ensureAbsoluteCanonical(canonicalFromJson || canonicalFromHead)
}

const normalizeCanonicalInHead = (head: string, canonicalUrl?: string) => {
  if (!canonicalUrl) return head

  return head.replace(
    /<link\s+rel="canonical"\s+href="([^"]+)"\s*\/?>/i,
    `<link rel="canonical" href="${canonicalUrl}" />`
  )
}

const hasCanonicalInHead = (head?: string) => /<link\s+rel="canonical"\s+href=/i.test(head || '')

const Meta = ({ pageProps }: MetaProps) => {
  const robotsContent = [
    pageProps?.meta_robots?.is_robots_noindex ? 'noindex' : 'index',
    pageProps?.meta_robots?.is_robots_nofollow ? 'nofollow' : 'follow'
  ]
    .filter(Boolean)
    .join(', ')

  const canonicalUrl = getCanonicalUrl(pageProps)
  const normalizedYoastHead = normalizeCanonicalInHead(pageProps?.yoast_head || '', canonicalUrl)
  const shouldInjectCanonical = canonicalUrl && !hasCanonicalInHead(normalizedYoastHead)

  return pageProps?.yoast_head ? (
    <Head>
      {parse(normalizedYoastHead)}
      {shouldInjectCanonical ? <link rel="canonical" href={canonicalUrl} /> : null}
      {robotsContent ? <meta name="robots" content={robotsContent} /> : null}
    </Head>
  ) : null
}

export default Meta
